import { Box } from '@mui/material'
import { FeatureFlagIdExportData } from 'c1gen/features'
import { AppV1AppUserServiceowner } from 'c1gen/roles'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { EmptyList } from 'components/core/empty-list'
import { C1Icon } from 'components/core/icons/C1Icon'
import List from 'components/core/list'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { RelativeTimestamp } from 'components/core/time/display'
import { useFeatureChecker, useHasBuiltinRole, useHasRole } from 'components/no-ui/capability'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { AppUserAccountType } from 'components/pages/app/users/account_type'
import { AppUserActionOptions, AppUserActionsDropdown } from 'components/pages/app/users/actions/AppUserActionsDropdown'
import { AppUserDisplayName, urlFromAppUser } from 'components/pages/app/users/app-user-display-name'
import { AppUserRolesWithFetch } from 'components/pages/app/users/roles/AppUserRolesWithFetch'
import { AppUserFilters, AppUserFilterKeys } from 'components/pages/app/users/search'
import { AppUserStatus } from 'components/pages/app/users/status/AppUserStatus'
import { ExportData } from 'components/pages/explore/query/ExportData'
import { UserDisplayNameWithFetch } from 'components/pages/user/user-display-name'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { RoleID } from 'lib/constants/system'
import { useInitialFilters } from 'lib/search'
import { CircleUser } from 'lucide-react'
import { useRouter } from 'next/router'
import { AppUserServiceSearchRequest } from 'pbts/c1/api/app/v1/app_user'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { FC, useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { searchAppUsersApi } from 'redux_toolkit/app-users'

const dropdownOptions = [
    AppUserActionOptions.setAccountType,
    AppUserActionOptions.setIdentity,
    AppUserActionOptions.clearIdentity,
    AppUserActionOptions.resetIdentity,
]

export enum AppUserListColumns {
    account = 'account',
    app = 'app',
    status = 'status',
    accountType = 'accountType',
    user = 'user',
    roles = 'roles',
    lastLogin = 'lastLogin',
    actions = 'actions',
}

export const appUserColsMap: Record<AppUserListColumns, C1TableReduxColumn<AppUserValue>> = {
    [AppUserListColumns.account]: {
        field: 'account',
        label: <FormattedMessage id="components_user_list_account" defaultMessage="Account" />,
        flex: 2,
        Component: ({ row }) => (
            <AppUserDisplayName appUser={row} showTooltip margin={false} />
        ),
    },
    [AppUserListColumns.app]: {
        field: 'app',
        label: <FormattedMessage id="components_user_list_app" defaultMessage="Application" />,
        Component: ({ row }) => (
            <Box>
                {row.app ? (
                    <AppDisplayName margin={false} app={row.app} />
                ) : (
                    <C1EmptyIndicator />
                )}
            </Box>
        ),
    },
    [AppUserListColumns.status]: {
        field: 'status',
        label: <FormattedMessage id="components_user_list_account_status" defaultMessage="Status" />,
        Component: ({ row }) => <AppUserStatus appUser={row} />,
    },
    [AppUserListColumns.accountType]: {
        field: 'accountType',
        label: <FormattedMessage id="components_user_list_account_type" defaultMessage="Account type" />,
        Component: ({ row }) => <AppUserAccountType appUser={row} />,
    },
    [AppUserListColumns.user]: {
        field: 'user',
        label: <FormattedMessage id="components_user_list_user" defaultMessage="User" />,
        Component: ({ row }) => (row.identityUser?.id ? (
            <UserDisplayNameWithFetch id={row.identityUser.id} margin={false} />
        ) : (
            <C1EmptyIndicator />
        )),
    },
    [AppUserListColumns.roles]: {
        field: 'roles',
        label: <FormattedMessage id="components_user_list_roles" defaultMessage="Roles" />,
        Component: ({ row }) => <AppUserRolesWithFetch appUserId={row.id} appId={row.appId} />,
    },
    [AppUserListColumns.lastLogin]: {
        field: 'lastLogin',
        label: <FormattedMessage id="components_user_list_last_login" defaultMessage="Last login" />,
        Component: ({ row }) => (
            <Box>
                {row.lastUsedAt && row.lastUsedAt.seconds ? (
                    <RelativeTimestamp variant="date_only" date={row.lastUsedAt} />
                ) : (
                    <C1EmptyIndicator />
                )}
            </Box>
        ),
    },
    [AppUserListColumns.actions]: {
        field: 'actions',
        isSticky: () => true,
        Component: ({ row }) => (
            <AppUserActionsDropdown appUser={row} options={dropdownOptions} />
        ),
    },
}

export type C1AppUsersListProps = {
    columns?: AppUserListColumns[]
    filter?: Partial<AppUserServiceSearchRequest>
    displayFilters?: AppUserFilterKeys[]
    onRowClick?: (event: React.MouseEvent | React.KeyboardEvent, appUser: AppUserValue) => void
    selections?: AppUserValue[]
    disabledSelections?: AppUserValue[]
    setSelections?: (appUsers: AppUserValue[]) => void
    emptyState?: React.ReactNode
    allowExport?: boolean
    extraFooterActions?: React.ReactElement
}

export const C1AppUsersList: FC<C1AppUsersListProps> = ({
    columns,
    filter: _filter,
    displayFilters,
    onRowClick,
    selections,
    disabledSelections,
    setSelections,
    emptyState: _emptyState,
    allowExport,
    extraFooterActions,
}) => {
    const intl = useIntl()
    const [page, setPage] = useState<number>(0)
    const hasExportDataFeature = useFeatureChecker(FeatureFlagIdExportData)
    const isReadOnlySuperAdmin = useHasBuiltinRole(RoleID.ReadOnlySuperAdmin)
    const hasRole = useHasRole(AppV1AppUserServiceowner)
    const canNavigateToAppUser = isReadOnlySuperAdmin || hasRole
    const router = useRouter()

    // Get initial filters from URL
    const initialFilter = useInitialFilters(AppUserServiceSearchRequest, ['appUserDomains', 'appUserTypes', 'appUserStatuses', 'userIds'])

    const [filter, setFilter] = useState<AppUserServiceSearchRequest>({
        ...initialFilter,
        ..._filter,
        expandMask: { paths: ['*'] },
    })

    // Sync state with the prop
    useLayoutEffect(() => {
        setFilter(() => ({ ...initialFilter, ..._filter, expandMask: { paths: ['*'] } }))
    }, [_filter, initialFilter])

    const combinedAppUserSearchRequestFilters = useMemo(() => {
        return {
            ...initialFilter,
            ...filter,
            expandMask: { paths: ['*'] },
        }
    }, [initialFilter, filter])

    const combinedAppUserSearchRequestFiltersDeps = JSON.stringify(combinedAppUserSearchRequestFilters)

    // NOTE: avoid render loops
    const appUserSearchRequest = useMemo(
        () => AppUserServiceSearchRequest.create(combinedAppUserSearchRequestFilters),
        [combinedAppUserSearchRequestFiltersDeps], // eslint-disable-line react-hooks/exhaustive-deps
    )

    const {
        data, isError, isLoading, isBackgroundLoading, getPage,
        canLoadMore, error, shouldInvalidateList,
    } = searchAppUsersApi.useSearch(
        appUserSearchRequest,
        { notify: true },
    )
    const invalidateAppUserList = searchAppUsersApi.useInvalidateSearch()

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    const appUserCols: Array<C1TableReduxColumn<AppUserValue>> = useMemo(() => {
        const columnsToUse = columns || [
            AppUserListColumns.account,
            AppUserListColumns.app,
            AppUserListColumns.status,
            AppUserListColumns.accountType,
            AppUserListColumns.user,
        ]

        return columnsToUse.map((col) => appUserColsMap[col])
    }, [columns])

    const emptyState = useMemo(() => {
        return _emptyState || <EmptyList
            title={<FormattedMessage
                id="components_app_user_list_no_app_users_found"
                defaultMessage="No accounts found"
            />
            }
            icon={<C1Icon icon={CircleUser} />}
        />
    }, [_emptyState])

    const handleOnRowClick = useCallback((_, row): void => {
        onRowClick ? onRowClick(_, row) : canNavigateToAppUser && router.push(urlFromAppUser(row))
    }, [canNavigateToAppUser, router, onRowClick])

    const getRowAriaLabel = useCallback((appUser: AppUserValue): string => {
        const accountName = appUser.displayName || appUser.email || ''
        const appName = appUser.app?.displayName || ''
        return intl.formatMessage(
            { id: 'app_user_list_row_aria_label', defaultMessage: 'Account {account} in {app}' },
            { account: accountName, app: appName },
        )
    }, [intl])

    const table = useMemo(
        () => (
            <C1TableRedux<AppUserValue>
                data={data}
                cols={appUserCols}
                canLoadMore={canLoadMore}
                isError={isError}
                isLoading={isLoading}
                isBackgroundLoading={isBackgroundLoading}
                onPageChange={onPageChange}
                error={error}
                shouldReload={shouldInvalidateList}
                onReload={invalidateAppUserList}
                page={page}
                onRowClick={handleOnRowClick}
                selections={selections}
                disabledSelections={disabledSelections}
                setSelections={setSelections}
                emptyState={emptyState}
                extraFooterActions={extraFooterActions}
                getRowAriaLabel={getRowAriaLabel}
            />
        ),
        [canLoadMore, data, error, invalidateAppUserList, isError, isLoading,
            onPageChange, page, shouldInvalidateList, appUserCols, handleOnRowClick,
            selections, disabledSelections, setSelections, emptyState, isBackgroundLoading,
            extraFooterActions, getRowAriaLabel],
    )

    const search = useMemo(() => {
        if (displayFilters && displayFilters.length > 0) {
            return (
                <AppUserFilters
                    filter={appUserSearchRequest}
                    setFilter={(updater): void => {
                        const updatedFilter = updater(filter)
                        setFilter(() => updatedFilter)
                        setPage(0)
                    }}
                    displayFilters={displayFilters}
                    emptyFilter={_filter || {}}
                />
            )
        }
        return undefined
    }, [displayFilters, appUserSearchRequest, filter, _filter])

    const exportDataActionRequest = useMemo(() => {
        return ExportDataRequest.create({
            queryId: QueryId.ALL_APP_USERS,
            request: {
                oneofKind: 'appUserServiceSearchRequest',
                appUserServiceSearchRequest: appUserSearchRequest,
            },
        })
    }, [appUserSearchRequest])

    const buttons = useMemo(() => {
        if (allowExport && hasExportDataFeature) {
            return <ExportData exportDataRequest={exportDataActionRequest} />
        }
        return undefined
    }, [allowExport, hasExportDataFeature, exportDataActionRequest])

    return (
        <List<AppUserValue>
            table={table}
            search={search}
            buttons={buttons}
        />
    )
}
